import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import {
  useGetApiCompaniesCurrentActionmodules,
  useGetApiCompaniesCurrentServicemodulesLeaderboardsServiceModuleId,
  useGetApiCompaniesCurrentServicemodulesLeaderboardsServiceModuleIdBoard,
} from '@/api/generated/company-management-user/company-management-user'
import type {
  ActionModuleResponse,
  LeaderboardServiceModuleModel,
  LeaderboardUserRankModelPaginatedResponse,
  OrderType,
  ServiceModuleModel,
} from '@/api/model'
import { PageLayout } from '@/components/layout/PageLayout'
import { PageHeader } from '@/components/layout/PageHeader'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/shadcn/select'
import { TableCard } from '@/components/common/table/TableCard'
import { PaginationFooter } from '@/components/common/table/PaginationFooter'
import { LeaderboardBoardTable } from './components/LeaderboardBoardTable'
import { LeaderboardDatePicker } from './components/LeaderboardDatePicker'
import { unwrap } from '@/utils/unwrap.ts'

interface Props {
  module: ServiceModuleModel
}

const DEFAULT_PAGE_SIZE = 10

/**
 * Leaderboard service module as its own page (card click → `?open`). The manager
 * picks one of the action modules assigned to the module and a reference date; the
 * table shows the server-paginated, sortable ranking of the action period that was
 * running on that reference date (the backend picks the most recent action module
 * instance with `StartDate <= reference date`). Assignment + ranking direction
 * are maintained **not** here but in the metadata editor.
 */
export function LeaderboardServiceModulePage({ module }: Props) {
  const { t } = useTranslation('service-modules-leaderboard')
  const id = module.id

  const moduleQuery = useGetApiCompaniesCurrentServicemodulesLeaderboardsServiceModuleId(id)
  const detail = unwrap<LeaderboardServiceModuleModel>(moduleQuery.data)
  const assignedIds = useMemo(() => detail?.assignedActionModuleIds ?? [], [detail])

  // Names of the assigned action modules (from the full list, like in the editor).
  const actionModulesQuery = useGetApiCompaniesCurrentActionmodules()
  const assignedActionModules = useMemo(() => {
    const all = unwrap<ActionModuleResponse[]>(actionModulesQuery.data) ?? []
    const set = new Set(assignedIds)
    return all.filter((a) => a.id != null && set.has(a.id))
  }, [actionModulesQuery.data, assignedIds])

  // No auto-select: the user picks an action module first; until then the dropdown
  // placeholder stays and the table remains empty.
  const [actionModuleId, setActionModuleId] = useState<number>()
  const [date, setDate] = useState<string>(() => dayjs().startOf('day').toISOString())
  const [sort, setSort] = useState<string>()
  const [order, setOrder] = useState<OrderType>()
  const [offset, setOffset] = useState(0)
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE)

  const boardQuery = useGetApiCompaniesCurrentServicemodulesLeaderboardsServiceModuleIdBoard(
    id,
    {
      actionModuleId,
      Offset: offset,
      Limit: limit,
      Sort: sort,
      Order: order,
      date,
    },
    { query: { enabled: actionModuleId != null && !!date } },
  )
  const board = unwrap<LeaderboardUserRankModelPaginatedResponse>(boardQuery.data)

  const hasAssignments = assignedActionModules.length > 0

  return (
    <PageLayout scrollEnabled>
      <PageHeader title={module.name ?? ''} description={t('leaderboard.description')} />

      {!hasAssignments ? (
        <div className="rounded-xl border bg-card p-6 text-sm text-muted-foreground">
          {t('leaderboard.noAssignments')}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-4">
            <Select
              value={actionModuleId != null ? String(actionModuleId) : undefined}
              onValueChange={(v) => {
                setActionModuleId(Number(v))
                setOffset(0)
              }}
            >
              <SelectTrigger className="w-64">
                <SelectValue placeholder={t('leaderboard.selectActionModule')} />
              </SelectTrigger>
              <SelectContent>
                {assignedActionModules.map((a) => (
                  <SelectItem key={a.id} value={String(a.id)}>
                    {a.name ?? t('leaderboard.actionModuleFallback', { id: a.id })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <LeaderboardDatePicker
              value={date}
              onChange={(iso) => {
                setDate(iso)
                setOffset(0)
              }}
            />
          </div>

          <TableCard>
            <div className="flex-1 overflow-auto scrollbar-custom relative">
              <LeaderboardBoardTable
                isLoading={boardQuery.isLoading || boardQuery.isFetching}
                isError={boardQuery.isError}
                rows={board?.items}
                currentSort={sort}
                currentOrder={order}
                onSort={(newSort, newOrder) => {
                  setSort(newSort)
                  setOrder(newOrder)
                  setOffset(0)
                }}
              />
            </div>

            <PaginationFooter
              offset={offset}
              limit={limit}
              totalCount={board?.totalCount}
              onOffsetChange={setOffset}
              onPageSizeChange={(pageSize) => {
                setLimit(pageSize)
                setOffset(0)
              }}
            />
          </TableCard>
        </>
      )}
    </PageLayout>
  )
}
